import { useState, useEffect } from 'react';
import { auth, db } from '../../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, updateDoc } from 'firebase/firestore';
import AdminLayout from '../../components/admin/AdminLayout';
import { formatDate } from '../../utils/helpers';
import { useTenantCollection, useTenantCrud, useClubContent } from '../../hooks/useTenantData';
import { useClub } from '../../contexts/ClubContext';

function ManageContent() {
  const [user] = useAuthState(auth);
  const { club, clubId } = useClub();
  
  // Use tenant-scoped hooks for announcements
  const { data: announcements, setData: setAnnouncements } = useTenantCollection('announcements');
  const { add: addAnnouncement, update: updateAnnouncement, remove: removeAnnouncement } = useTenantCrud('announcements');
  
  // Use club content hooks for homepage and club info
  const { content: homepageContent, loading: homepageLoading, save: saveHomepageContent } = useClubContent('homepage');
  const { content: clubInfoContent, loading: clubInfoLoading, save: saveClubInfoContent } = useClubContent('clubInfo');
  const [activeTab, setActiveTab] = useState('homepage');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Homepage content state (will be populated from hook)
  const [homepage, setHomepage] = useState({
    heroTitle: '',
    heroSubtitle: 'Developing Champions for Tomorrow',
    statPlayers: '500+',
    statAgeGroups: '8',
    statCoaches: '50+',
    statTournaments: '25+',
    highlightVideo1: '',
    highlightVideo2: '',
    highlightTitle1: 'Match Highlights',
    highlightTitle2: 'Training Session'
  });

  // Announcements modal state
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    type: 'info',
    pinned: false
  });

  // Club info state
  const [clubInfo, setClubInfo] = useState({
    mission: 'To develop young footballers in Trinidad & Tobago through professional coaching, competitive play, and character building.',
    vision: 'To be the premier youth football academy in the Caribbean, producing players who excel on and off the field.',
    values: 'Excellence, Discipline, Teamwork, Respect, Passion',
    history: '',
    phone: '',
    email: '',
    address: '',
    hours: ''
  });

  // Branding/Colors state
  const [branding, setBranding] = useState({
    primaryColor: '#1e3a5f',
    secondaryColor: '#ce1126'
  });

  // Sync branding from club
  useEffect(() => {
    if (club) {
      setBranding({
        primaryColor: club.primaryColor || '#1e3a5f',
        secondaryColor: club.secondaryColor || '#ce1126'
      });
    }
  }, [club]);

  // Sync homepage content from hook
  useEffect(() => {
    if (homepageContent) {
      setHomepage(prev => ({ 
        ...prev, 
        ...homepageContent,
        heroTitle: homepageContent.heroTitle || club?.name || ''
      }));
    }
  }, [homepageContent, club?.name]);

  // Sync club info content from hook
  useEffect(() => {
    if (clubInfoContent) {
      setClubInfo(prev => ({ ...prev, ...clubInfoContent }));
    }
  }, [clubInfoContent]);

  // Sort announcements: pinned first, then by date
  const sortedAnnouncements = [...announcements].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
    const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
    return dateB - dateA;
  });

  const saveHomepage = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Save homepage content (tenant-scoped)
      await saveHomepageContent(homepage);
      setSuccess('Homepage content saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving homepage:', error);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveClubInfo = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Save club info content (tenant-scoped)
      await saveClubInfoContent(clubInfo);
      setSuccess('Club info saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving club info:', error);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveBranding = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Update club document directly with colors
      await updateDoc(doc(db, 'clubs', clubId), {
        primaryColor: branding.primaryColor,
        secondaryColor: branding.secondaryColor,
        updatedAt: new Date()
      });
      
      // Apply colors immediately
      const root = document.documentElement;
      root.style.setProperty('--club-primary', branding.primaryColor);
      root.style.setProperty('--club-secondary', branding.secondaryColor);
      
      setSuccess('Branding colors saved successfully! Refresh to see full changes.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error saving branding:', error);
      setError('Failed to save branding. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const openAddAnnouncement = () => {
    setEditingAnnouncement(null);
    setAnnouncementForm({ title: '', content: '', type: 'info', pinned: false });
    setShowAnnouncementModal(true);
  };

  const openEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setAnnouncementForm({
      title: announcement.title || '',
      content: announcement.content || '',
      type: announcement.type || 'info',
      pinned: announcement.pinned || false
    });
    setShowAnnouncementModal(true);
  };

  const saveAnnouncement = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const announcementData = {
        title: announcementForm.title,
        content: announcementForm.content,
        type: announcementForm.type,
        pinned: announcementForm.pinned
      };

      if (editingAnnouncement) {
        // Update announcement (tenant-scoped)
        const updated = await updateAnnouncement(editingAnnouncement.id, announcementData);
        setAnnouncements(announcements.map(a =>
          a.id === editingAnnouncement.id ? updated : a
        ));
      } else {
        // Add new announcement (tenant-scoped)
        const newAnn = await addAnnouncement(announcementData);
        setAnnouncements([newAnn, ...announcements]);
      }

      setShowAnnouncementModal(false);
      setSuccess('Announcement saved!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving announcement:', error);
      setError('Failed to save announcement.');
    } finally {
      setSaving(false);
    }
  };

  const deleteAnnouncement = async (id, title) => {
    if (!window.confirm(`Delete announcement "${title}"?`)) return;

    try {
      // Delete announcement (tenant-scoped)
      await removeAnnouncement(id);
      setAnnouncements(announcements.filter(a => a.id !== id));
    } catch (error) {
      console.error('Error deleting announcement:', error);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'success': return '#22c55e';
      case 'warning': return '#f59e0b';
      case 'urgent': return '#ef4444';
      default: return '#3b82f6';
    }
  };

  return (
    <AdminLayout title="Manage Content" subtitle={`Edit website content and announcements for ${club?.name || 'your club'}`}>
      {success && <div className="form-success">{success}</div>}
      {error && <div className="form-error">{error}</div>}

          {/* Tabs */}
          <div className="content-tabs">
            <button
              className={`content-tab ${activeTab === 'homepage' ? 'active' : ''}`}
              onClick={() => setActiveTab('homepage')}
            >
              Homepage
            </button>
            <button
              className={`content-tab ${activeTab === 'announcements' ? 'active' : ''}`}
              onClick={() => setActiveTab('announcements')}
            >
              Announcements ({sortedAnnouncements.length})
            </button>
            <button
              className={`content-tab ${activeTab === 'clubinfo' ? 'active' : ''}`}
              onClick={() => setActiveTab('clubinfo')}
            >
              Club Info
            </button>
            <button
              className={`content-tab ${activeTab === 'branding' ? 'active' : ''}`}
              onClick={() => setActiveTab('branding')}
            >
              Branding
            </button>
          </div>

          {/* Homepage Tab */}
          {activeTab === 'homepage' && (
            <div className="content-section">
              <div className="admin-card">
                <div className="card-header">
                  <h2>Hero Section</h2>
                </div>
                <div className="card-content">
                  <div className="form-group">
                    <label>Hero Title</label>
                    <input
                      type="text"
                      value={homepage.heroTitle}
                      onChange={(e) => setHomepage({ ...homepage, heroTitle: e.target.value })}
                      placeholder="PRO SERIES FC"
                    />
                  </div>
                  <div className="form-group">
                    <label>Hero Subtitle</label>
                    <input
                      type="text"
                      value={homepage.heroSubtitle}
                      onChange={(e) => setHomepage({ ...homepage, heroSubtitle: e.target.value })}
                      placeholder="Developing Champions for Tomorrow"
                    />
                  </div>
                </div>
              </div>

              <div className="admin-card">
                <div className="card-header">
                  <h2>Stats Section</h2>
                </div>
                <div className="card-content">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Players Count</label>
                      <input
                        type="text"
                        value={homepage.statPlayers}
                        onChange={(e) => setHomepage({ ...homepage, statPlayers: e.target.value })}
                        placeholder="500+"
                      />
                    </div>
                    <div className="form-group">
                      <label>Age Groups</label>
                      <input
                        type="text"
                        value={homepage.statAgeGroups}
                        onChange={(e) => setHomepage({ ...homepage, statAgeGroups: e.target.value })}
                        placeholder="8"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Coaches Count</label>
                      <input
                        type="text"
                        value={homepage.statCoaches}
                        onChange={(e) => setHomepage({ ...homepage, statCoaches: e.target.value })}
                        placeholder="50+"
                      />
                    </div>
                    <div className="form-group">
                      <label>Tournaments</label>
                      <input
                        type="text"
                        value={homepage.statTournaments}
                        onChange={(e) => setHomepage({ ...homepage, statTournaments: e.target.value })}
                        placeholder="25+"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="admin-card">
                <div className="card-header">
                  <h2>Highlight Videos</h2>
                </div>
                <div className="card-content">
                  <div className="form-group">
                    <label>Video 1 Title</label>
                    <input
                      type="text"
                      value={homepage.highlightTitle1}
                      onChange={(e) => setHomepage({ ...homepage, highlightTitle1: e.target.value })}
                      placeholder="Match Highlights"
                    />
                  </div>
                  <div className="form-group">
                    <label>Video 1 YouTube URL</label>
                    <input
                      type="url"
                      value={homepage.highlightVideo1}
                      onChange={(e) => setHomepage({ ...homepage, highlightVideo1: e.target.value })}
                      placeholder="https://www.youtube.com/embed/..."
                    />
                    <span className="form-hint">Use YouTube embed URL (e.g., https://www.youtube.com/embed/VIDEO_ID)</span>
                  </div>
                  <div className="form-group">
                    <label>Video 2 Title</label>
                    <input
                      type="text"
                      value={homepage.highlightTitle2}
                      onChange={(e) => setHomepage({ ...homepage, highlightTitle2: e.target.value })}
                      placeholder="Training Session"
                    />
                  </div>
                  <div className="form-group">
                    <label>Video 2 YouTube URL</label>
                    <input
                      type="url"
                      value={homepage.highlightVideo2}
                      onChange={(e) => setHomepage({ ...homepage, highlightVideo2: e.target.value })}
                      placeholder="https://www.youtube.com/embed/..."
                    />
                  </div>
                </div>
              </div>

              <div className="content-actions">
                <button className="btn-save" onClick={saveHomepage} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Homepage Content'}
                </button>
              </div>
            </div>
          )}

          {/* Announcements Tab */}
          {activeTab === 'announcements' && (
            <div className="content-section">
              <div className="section-header">
                <h2>Announcements</h2>
                <button className="btn-add" onClick={openAddAnnouncement}>
                  + New Announcement
                </button>
              </div>

              {sortedAnnouncements.length === 0 ? (
                <div className="admin-card">
                  <div className="empty-state">
                    <span className="empty-icon">📢</span>
                    <h3>No Announcements</h3>
                    <p>Create your first announcement to display on the website.</p>
                    <button className="btn-add" onClick={openAddAnnouncement}>
                      + New Announcement
                    </button>
                  </div>
                </div>
              ) : (
                <div className="announcements-list">
                  {sortedAnnouncements.map(announcement => (
                    <div key={announcement.id} className="announcement-card">
                      <div
                        className="announcement-type-bar"
                        style={{ backgroundColor: getTypeColor(announcement.type) }}
                      ></div>
                      <div className="announcement-content">
                        <div className="announcement-header">
                          <h3>
                            {announcement.pinned && <span className="pin-icon">📌</span>}
                            {announcement.title}
                          </h3>
                          <span className="announcement-date">{formatDate(announcement.createdAt)}</span>
                        </div>
                        <p>{announcement.content}</p>
                        <div className="announcement-meta">
                          <span
                            className="type-badge"
                            style={{ backgroundColor: getTypeColor(announcement.type) }}
                          >
                            {announcement.type}
                          </span>
                        </div>
                      </div>
                      <div className="announcement-actions">
                        <button className="btn-edit" onClick={() => openEditAnnouncement(announcement)}>
                          Edit
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => deleteAnnouncement(announcement.id, announcement.title)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Club Info Tab */}
          {activeTab === 'clubinfo' && (
            <div className="content-section">
              <div className="admin-card">
                <div className="card-header">
                  <h2>Mission, Vision & Values</h2>
                </div>
                <div className="card-content">
                  <div className="form-group">
                    <label>Mission Statement</label>
                    <textarea
                      value={clubInfo.mission}
                      onChange={(e) => setClubInfo({ ...clubInfo, mission: e.target.value })}
                      placeholder="Our mission..."
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Vision Statement</label>
                    <textarea
                      value={clubInfo.vision}
                      onChange={(e) => setClubInfo({ ...clubInfo, vision: e.target.value })}
                      placeholder="Our vision..."
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Core Values</label>
                    <input
                      type="text"
                      value={clubInfo.values}
                      onChange={(e) => setClubInfo({ ...clubInfo, values: e.target.value })}
                      placeholder="Excellence, Discipline, Teamwork..."
                    />
                    <span className="form-hint">Separate values with commas</span>
                  </div>
                  <div className="form-group">
                    <label>Club History</label>
                    <textarea
                      value={clubInfo.history}
                      onChange={(e) => setClubInfo({ ...clubInfo, history: e.target.value })}
                      placeholder="Founded in..."
                      rows={4}
                    />
                  </div>
                </div>
              </div>

              <div className="admin-card">
                <div className="card-header">
                  <h2>Contact Information</h2>
                </div>
                <div className="card-content">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={clubInfo.phone}
                        onChange={(e) => setClubInfo({ ...clubInfo, phone: e.target.value })}
                        placeholder="(868) 123-4567"
                      />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={clubInfo.email}
                        onChange={(e) => setClubInfo({ ...clubInfo, email: e.target.value })}
                        placeholder="info@proseriesfc.com"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <input
                      type="text"
                      value={clubInfo.address}
                      onChange={(e) => setClubInfo({ ...clubInfo, address: e.target.value })}
                      placeholder="123 Football Drive, Port of Spain"
                    />
                  </div>
                  <div className="form-group">
                    <label>Office Hours</label>
                    <input
                      type="text"
                      value={clubInfo.hours}
                      onChange={(e) => setClubInfo({ ...clubInfo, hours: e.target.value })}
                      placeholder="Mon-Fri: 9AM-5PM, Sat: 8AM-12PM"
                    />
                  </div>
                </div>
              </div>

              <div className="content-actions">
                <button className="btn-save" onClick={saveClubInfo} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Club Info'}
                </button>
              </div>
            </div>
          )}

          {/* Branding Tab */}
          {activeTab === 'branding' && (
            <div className="content-section">
              <div className="admin-card">
                <div className="card-header">
                  <h2>Club Colors</h2>
                </div>
                <div className="card-content">
                  <p style={{ color: '#9ca3af', marginBottom: '20px' }}>
                    Customize your club's colors. These colors will be applied to buttons, links, and accents throughout your club's website.
                  </p>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Primary Color</label>
                      <div className="color-input-wrapper">
                        <input
                          type="color"
                          value={branding.primaryColor}
                          onChange={(e) => setBranding({ ...branding, primaryColor: e.target.value })}
                          className="color-picker"
                        />
                        <input
                          type="text"
                          value={branding.primaryColor}
                          onChange={(e) => setBranding({ ...branding, primaryColor: e.target.value })}
                          placeholder="#1e3a5f"
                          className="color-text"
                        />
                      </div>
                      <span className="form-hint">Used for borders, backgrounds, and secondary elements</span>
                    </div>
                    <div className="form-group">
                      <label>Secondary Color</label>
                      <div className="color-input-wrapper">
                        <input
                          type="color"
                          value={branding.secondaryColor}
                          onChange={(e) => setBranding({ ...branding, secondaryColor: e.target.value })}
                          className="color-picker"
                        />
                        <input
                          type="text"
                          value={branding.secondaryColor}
                          onChange={(e) => setBranding({ ...branding, secondaryColor: e.target.value })}
                          placeholder="#ce1126"
                          className="color-text"
                        />
                      </div>
                      <span className="form-hint">Used for buttons, links, and accent highlights</span>
                    </div>
                  </div>

                  {/* Color Preview */}
                  <div className="color-preview">
                    <h3>Preview</h3>
                    <div className="preview-container">
                      <div className="preview-item">
                        <div 
                          className="preview-swatch" 
                          style={{ backgroundColor: branding.primaryColor }}
                        ></div>
                        <span>Primary</span>
                      </div>
                      <div className="preview-item">
                        <div 
                          className="preview-swatch" 
                          style={{ backgroundColor: branding.secondaryColor }}
                        ></div>
                        <span>Secondary</span>
                      </div>
                      <div className="preview-item">
                        <button 
                          className="preview-btn"
                          style={{ 
                            background: `linear-gradient(135deg, ${branding.secondaryColor} 0%, ${branding.secondaryColor}dd 100%)`,
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          Sample Button
                        </button>
                      </div>
                      <div className="preview-item">
                        <div 
                          style={{ 
                            border: `2px solid ${branding.primaryColor}`,
                            padding: '10px 20px',
                            borderRadius: '6px',
                            color: '#9ca3af'
                          }}
                        >
                          Secondary Style
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="content-actions">
                <button className="btn-save" onClick={saveBranding} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Branding'}
                </button>
              </div>
            </div>
          )}

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div className="modal-overlay" onClick={() => setShowAnnouncementModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}</h2>
              <button className="modal-close" onClick={() => setShowAnnouncementModal(false)}>×</button>
            </div>
            <form onSubmit={saveAnnouncement}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={announcementForm.title}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                    placeholder="Announcement title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Content *</label>
                  <textarea
                    value={announcementForm.content}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, content: e.target.value })}
                    placeholder="Announcement content..."
                    rows={4}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Type</label>
                    <select
                      value={announcementForm.type}
                      onChange={(e) => setAnnouncementForm({ ...announcementForm, type: e.target.value })}
                    >
                      <option value="info">Info</option>
                      <option value="success">Success</option>
                      <option value="warning">Warning</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={announcementForm.pinned}
                        onChange={(e) => setAnnouncementForm({ ...announcementForm, pinned: e.target.checked })}
                      />
                      Pin to top
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowAnnouncementModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Announcement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ManageContent;
